/* ------------------------------------------------------------------ */
/* Seção 1 — Fontes Públicas Unificadas                                 */
/* ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { StatusBadge, type Status } from './badges';

interface FontePublica {
  codigo: string;
  nome: string;
  status: Status;
  subtitulo: string;
  oQueEntrega: string[];
  usoPratico: string[];
  requisitos: string[];
  statusDetalhe: string;
  limitacoes: string[];
  comoFunciona: string[];
  pendencias: string[];
  duvidasAbertas?: string[];
  fontes?: Array<{ label: string; url: string }>;
}

const fontesPublicas: FontePublica[] = [
  {
    codigo: 'J01',
    nome: 'DJEN — Domicílio Judicial Eletrônico',
    status: 'bypass',
    subtitulo: 'Centraliza intimações eletrônicas de todos os tribunais cadastrados no CNJ.',
    oQueEntrega: [
      'Intimações endereçadas à OAB monitorada (ex.: Bárbara OAB 87113/RS)',
      'Número do processo (CNJ), texto, prazo, tribunal e tipo de ato',
      'Criação automática de tarefas com prazo fatal via J07',
      'Notificação interna ao advogado sobre nova intimação (F05)',
      'Normaliza formato legado (snake_case) e novo (camelCase) do DJEN',
    ],
    usoPratico: [
      'Aba "Intimações" do processo (D02) — preenchida automaticamente com cada comunicação recebida',
      'Tarefa com prazo fatal gerada no D03 quando o parser (J09) identifica prazo no texto',
      'Notificação push + email + in-app ao advogado responsável (F05)',
      'Card "Última intimação" atualizado na lista de processos',
      'Dashboard "Próximas intimações" — indicador de prazo com contagem de dias',
      'Prazo conta a partir do DJEN (Res. CNJ 569/2024) — é a contagem oficial',
    ],
    requisitos: [
      'Token SSO CNJ do advogado cadastrado no DJEN',
      'IP residencial brasileiro — CDN do CNJ bloqueia IPs de datacenter',
      'Hoje: script Node rodando em máquina local com Task Scheduler Windows',
      'Plano: migrar para VPS brasileira (mesmo host do RPA eProc)',
    ],
    statusDetalhe:
      'Funcional via bridge local. Script `tools/djen-bridge/pull-and-push.mjs` roda na máquina local (IP residencial BR), faz polling no DJEN e envia resultado à API via endpoints internos protegidos por X-Internal-Token. Cron interno desligado em produção (DJEN_AUTO_POLL_ENABLED=false).',
    limitacoes: [
      'Polling, não webhook — sem recebimento em tempo real',
      'Dependência de PC local ligado no horário do Task Scheduler',
      'Sem escalabilidade para múltiplos advogados sem infraestrutura dedicada',
      'Credencial CNJ formal (sistemasnacionais@cnj.jus.br) ainda pendente',
    ],
    comoFunciona: [
      '1. Script Node (tools/djen-bridge/pull-and-push.mjs) roda na máquina local com IP residencial BR',
      '2. Faz GET no DJEN/CNJ com token SSO do advogado cadastrado',
      '3. Envia resultado via POST para /api/internal/djen/poll-plan + /api/internal/djen/ingest',
      '4. Endpoints internos protegidos por X-Internal-Token (env INTERNAL_BRIDGE_TOKEN)',
      '5. API processa, persiste intimações no banco e cria tarefas via J07',
      '6. Task Scheduler Windows dispara o script em horários configurados',
    ],
    pendencias: [
      'Credencial formal de sistema no CNJ — desbloqueia polling direto do servidor sem bridge',
      'Migrar bridge para VPS brasileira (junto com RPA eProc) — elimina dependência de PC local',
    ],
    duvidasAbertas: [
      'O CNJ tem planos de allowlist de IPs de provedores parceiros? Não há documentação pública',
      'O DJEN enviará intimações via webhook no futuro? Documentação atual não menciona',
    ],
    fontes: [
      { label: 'Portal DJEN — CNJ', url: 'https://comunica.pje.jus.br/' },
      { label: 'Documentação da API DJEN (CNJ)', url: 'https://comunicaapi.pje.jus.br/api/v1/api-docs' },
      { label: 'Resolução CNJ 569/2024 (vigência 16/05/2025 — centraliza comunicações)', url: 'https://atos.cnj.jus.br/atos/detalhar/5979' },
    ],
  },
  {
    codigo: 'J02',
    nome: 'DataJud — Base Nacional de Dados do Poder Judiciário',
    status: 'ativo',
    subtitulo: 'API pública REST do CNJ com metadados de mais de 80 milhões de processos judiciais.',
    oQueEntrega: [
      'Capa processual: tribunal, classe, assunto, valor da causa, data de distribuição',
      'Movimentações padronizadas pela Tabela Processual Unificada (TPU) do CNJ',
      'Auto-preenchimento do formulário de cadastro de processo (D02)',
      'Cache de 24h para evitar chamadas repetidas à mesma consulta',
      'Cobre todos os tribunais que enviam dados ao CNJ',
    ],
    usoPratico: [
      'Formulário de cadastro de processo — ao colar o número CNJ, preenche automaticamente: classe, assunto, segmento, tribunal, comarca, vara, valor da causa e partes',
      'Aba "Andamentos" do processo — movimentações padronizadas com badge "via DataJud"',
      'Limitação visível ao usuário: latência de 24-72h por tribunal; sem inteiro teor de peças',
    ],
    requisitos: [
      'Chave pública anônima no header Authorization: APIKey',
      'Nenhum certificado ou credencial especial — acesso público',
      'LXC 107 acessa normalmente (DataJud está em infra separada do DJEN, sem bloqueio de IP)',
    ],
    statusDetalhe:
      'Totalmente ativo. Validado em produção: LXC 107 (datacenter americano) conecta normalmente — DataJud está em infraestrutura separada do DJEN, sem bloqueio de IP. Único caso de API CNJ sem problemas de acesso.',
    limitacoes: [
      'Não indexa lista de processos por OAB/advogado',
      'Não expõe CPF/CNPJ das partes (LGPD)',
      'Não entrega autos digitais (petições, decisões em PDF)',
      'Atualização pode ter atraso de dias a semanas por tribunal',
      'Processos sigilosos ou com segredo de justiça não aparecem',
    ],
    comoFunciona: [
      '1. Frontend chama /api/processos/datajud/:numero com o número CNJ formatado',
      '2. NestJS faz GET para https://api-publica.datajud.cnj.jus.br/api_publica_{tribunal}/_search',
      '3. Resposta cacheada por 24h no banco (migration 0015)',
      '4. Dados mapeados para o formulário D02',
    ],
    pendencias: [
      'Expandir uso do DataJud para popular movimentações no detalhe do processo (hoje só auto-preenche no cadastro)',
      'Avaliar rate limits para uso mais intensivo (chave anônima tem limites não publicados)',
    ],
    fontes: [
      { label: 'Portal DataJud — CNJ', url: 'https://www.cnj.jus.br/sistemas/datajud/' },
      { label: 'API Pública DataJud — Documentação', url: 'https://datajud-wiki.cnj.jus.br/api-publica/acesso' },
      { label: 'Resolução CNJ 331/2020', url: 'https://atos.cnj.jus.br/atos/detalhar/3412' },
    ],
  },
  {
    codigo: 'J03',
    nome: 'DJE — Diário da Justiça Eletrônico',
    status: 'bloqueado',
    subtitulo: 'Publicação oficial de cada tribunal com intimações, despachos e decisões em formato público.',
    oQueEntrega: [
      'Publicações oficiais de cada tribunal (quando implementado)',
      'Intimações, despachos e decisões em formato público por tribunal',
      'Schema de banco preparado (migration 0014, J04 como base)',
      'Arquitetura de adapter definida (J05)',
    ],
    usoPratico: [
      'Aba "Citações" do processo — citação pessoal com status: em curso / ciente / expirado',
      'Tarefa com prazo de 10 dias gerada automaticamente se status expirar sem leitura + notificação F05',
      'Dashboard "Citações pendentes" — visão consolidada por escritório',
      'Nenhum campo preenchido atualmente — integração bloqueada (depende de J04 + J05)',
    ],
    requisitos: [
      'Certificado digital A1 em runtime (via J04 Cert Manager)',
      'Adapter J05 por tribunal — cada portal tem URL e formato próprios',
      'Infraestrutura com IP brasileiro (mesma exigência do eProc)',
    ],
    statusDetalhe:
      'Bloqueado. Nenhum adapter implementado em produção. Ao contrário do DJEN (endereço digital individualizado), o DJE é público por tribunal — mas cada tribunal tem portal próprio, formato e URL distintos. Depende de J04 ativo e J05 implementado.',
    limitacoes: [
      'Sem API padronizada entre tribunais',
      'Cada tribunal exige adapter próprio',
      'Acesso a seções restritas exige certificado A1/A3 em runtime',
    ],
    comoFunciona: [
      'Fluxo planejado (não implementado):',
      '1. J04 Cert Manager fornece certificado digital em runtime',
      '2. Adapter J05 específico do tribunal autentica no portal do DJE',
      '3. Scraping ou API do tribunal extrai publicações relevantes',
      '4. J09 Publication Parser identifica tipo de ato e prazo',
      '5. J07 Deadline Calculator gera tarefa se houver prazo',
    ],
    pendencias: [
      'Certificado digital da Bárbara em nuvem (pré-requisito via J04)',
      'Implementar adapter J05 para eProc TJRS como primeiro tribunal',
    ],
    fontes: [
      { label: 'Lei 11.419/2006 (informatização do processo)', url: 'https://www.planalto.gov.br/ccivil_03/_ato2004-2006/2006/lei/l11419.htm' },
      { label: 'CNJ — Diários da Justiça', url: 'https://www.cnj.jus.br/diarios-da-justica-eletronicos/' },
    ],
  },
];

function FonteCard({ item }: { item: FontePublica }) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="rounded-md bg-[var(--surface-muted)] px-2 py-1 font-mono text-xs font-bold text-[var(--fg-muted)] select-none whitespace-nowrap mt-0.5">
                {item.codigo}
              </span>
              <div>
                <CardTitle className="text-base leading-snug">{item.nome}</CardTitle>
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">{item.subtitulo}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <StatusBadge status={item.status} />
              <span className="text-[var(--fg-muted)] text-sm select-none">
                {open ? '▲' : '▼'}
              </span>
            </div>
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="border-t border-[var(--card-border)] pt-4 space-y-5">
          <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{item.statusDetalhe}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-400">
                <span>✓</span> O que entrega
              </p>
              <ul className="space-y-1.5">
                {item.oQueEntrega.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-green-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                <span>→</span> Requisitos
              </p>
              <ul className="space-y-1.5">
                {item.requisitos.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg-muted)]">
                    <span className="mt-0.5 shrink-0">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {item.usoPratico.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-400">
                <span>◈</span> Uso prático no app
              </p>
              <ul className="space-y-1.5">
                {item.usoPratico.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-blue-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.limitacoes.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-400">
                <span>✕</span> Limitações
              </p>
              <ul className="space-y-1.5">
                {item.limitacoes.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-red-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
              <span>→</span> Como funciona
            </p>
            <ol className="space-y-1.5">
              {item.comoFunciona.map((txt, i) => (
                <li key={i} className="text-sm text-[var(--fg-muted)] leading-relaxed">{txt}</li>
              ))}
            </ol>
          </div>

          {item.pendencias.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-yellow-400">
                <span>!</span> Pendências
              </p>
              <ul className="space-y-1.5">
                {item.pendencias.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-yellow-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.duvidasAbertas && item.duvidasAbertas.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                <span>?</span> Dúvidas em aberto
              </p>
              <ul className="space-y-1.5">
                {item.duvidasAbertas.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg-muted)]">
                    <span className="mt-0.5 shrink-0">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.fontes && item.fontes.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
                <span>↗</span> Fontes
              </p>
              <ul className="space-y-1.5">
                {item.fontes.map((fonte) => (
                  <li key={fonte.url} className="flex gap-2 text-sm">
                    <span className="mt-0.5 shrink-0 text-[var(--accent)]">•</span>
                    <a
                      href={fonte.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent)] hover:underline break-all"
                    >
                      {fonte.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SecaoFontesPublicas() {
  return (
    <section aria-label="Fontes públicas unificadas">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-xs font-bold text-[var(--fg-muted)]">
            SEÇÃO 1
          </span>
          <h2 className="text-xl font-bold tracking-tight">Fontes Públicas Unificadas</h2>
        </div>
        <p className="max-w-2xl text-sm text-[var(--fg-muted)] leading-relaxed">
          APIs e portais do CNJ que centralizam dados processuais de todos os tribunais.
          Nenhuma exige certificado digital para acesso básico.
          A Resolução CNJ 569/2024 (vigência 16/05/2025) unificou as <strong className="text-[var(--fg)]">comunicações</strong> no DJEN — os sistemas processuais continuam fragmentados por tribunal.
        </p>
      </div>

      <div className="space-y-3">
        {fontesPublicas.map((item) => (
          <FonteCard key={item.codigo} item={item} />
        ))}
      </div>
    </section>
  );
}
